import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ClientQuote,
  type GetCustomerQuoteResponse,
  type RegenerateCustomerQuoteResponse,
  type PatchCustomerQuoteResponse,
  type DeleteCustomerQuoteResponse,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
  quote: ClientQuote;   // the row's quote (metadata; html fetched on open)
  rfqId?: string;       // public ID of the parent RFQ (for query invalidation)
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};

function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function remainingValidityDays(validUntil: string): number {
  const ms = new Date(validUntil).getTime() - Date.now();
  return Math.max(1, Math.round(ms / 86_400_000));
}

export function EditQuoteModal({ open, onClose, quote, rfqId }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [cq, setCq] = useState<ClientQuote>(quote);
  const [html, setHtml] = useState<string | null>(quote.html ?? null);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [markupPct, setMarkupPct] = useState<number>(quote.markupPct);
  const [validityDays, setValidityDays] = useState<number>(remainingValidityDays(quote.validUntil));
  const [qty, setQty] = useState<number>(quote.qty);
  const dismiss = useModalDismiss(onClose);

  const isDraft = cq.status === 'draft';

  // Margin guard — same as the create modal, computed from the saved snapshot.
  const vendorTotalCents = cq.vendorUnitPriceCents * cq.qty;
  const marginCents = cq.customerTotalCents - vendorTotalCents;
  const marginPct = vendorTotalCents > 0 ? (marginCents / vendorTotalCents) * 100 : 0;

  // Fetch the full HTML snapshot when the modal opens (list rows omit it).
  useEffect(() => {
    if (!open) return;
    setCq(quote);
    setMarkupPct(quote.markupPct);
    setValidityDays(remainingValidityDays(quote.validUntil));
    setQty(quote.qty);
    setLoadError(null);
    let cancelled = false;
    setLoadingHtml(true);
    api<GetCustomerQuoteResponse>(`/api/customer-quotes/${quote.id}`, { tokenGetter: () => getToken() })
      .then((data) => {
        if (cancelled) return;
        setCq(data.clientQuote);
        setHtml(data.clientQuote.html ?? null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError((e as { message?: string })?.message ?? 'Failed to load quote');
      })
      .finally(() => { if (!cancelled) setLoadingHtml(false); });
    return () => { cancelled = true; };
    // Key on quote.id (not the object) so a background refetch — including the
    // ones our own mutations trigger via invalidation — doesn't re-run this and
    // wipe the markup/validity inputs or reload the iframe mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quote.id]);

  // Render html into the iframe whenever it changes.
  useEffect(() => {
    if (!html || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
  }, [html]);

  function invalidateLists() {
    qc.invalidateQueries({ queryKey: ['client-quotes'] });
    if (rfqId) qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  }

  const regenerate = useMutation({
    mutationFn: async () => {
      return api<RegenerateCustomerQuoteResponse>(`/api/customer-quotes/${cq.id}/regenerate`, {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ markupPctOverride: markupPct, validityDays, qty }),
      });
    },
    onSuccess: (data) => {
      setCq(data.clientQuote);
      if (data.clientQuote.html) setHtml(data.clientQuote.html);
      invalidateLists();
    },
  });

  const patch = useMutation({
    mutationFn: async (newStatus: 'sent' | 'accepted' | 'rejected' | 'expired') => {
      return api<PatchCustomerQuoteResponse>(`/api/customer-quotes/${cq.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ status: newStatus }),
      });
    },
    onSuccess: (data) => {
      setCq(data.clientQuote);
      invalidateLists();
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      return api<DeleteCustomerQuoteResponse>(`/api/customer-quotes/${cq.id}`, {
        method: 'DELETE',
        tokenGetter: () => getToken(),
      });
    },
    onSuccess: () => {
      invalidateLists();
      onClose();
    },
  });

  const downloadHtml = () => {
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${cq.ref}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyHtml = async () => {
    if (!html) return;
    try { await navigator.clipboard.writeText(html); }
    catch { window.prompt('Copy the HTML manually:', html.slice(0, 100) + '…'); }
  };

  const openInNewTab = () => {
    if (!html) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">
              {isDraft ? 'Edit draft quote' : 'View quote'} · <span className="font-mono text-sm">{cq.ref}</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isDraft
                ? 'Adjust markup / validity, regenerate to apply the latest template, then mark as sent.'
                : `This quote is ${cq.status} and is locked. You can still view, download, or copy it.`}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[320px_1fr]">
          <aside className="border-b lg:border-r border-border overflow-auto p-5 space-y-5 bg-muted/20">
            <div className="border border-border rounded p-3 bg-background space-y-1.5 text-sm">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Current · {cq.currency}
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-semibold text-primary">{cq.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Customer total</span>
                <span className="font-mono font-semibold">{fmtMoney(cq.customerTotalCents, cq.currency)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Markup</span>
                <span className="font-mono">{cq.markupPct}% ({cq.markupSource})</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Margin</span>
                <span className="font-mono">{fmtMoney(marginCents, cq.currency)} ({marginPct.toFixed(1)}%)</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Valid until</span>
                <span>{new Date(cq.validUntil).toLocaleDateString()}</span>
              </div>
            </div>

            {isDraft && marginCents <= 0 && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                <strong>No margin.</strong> This quote sells at {marginCents < 0 ? 'a loss' : 'cost'} ({fmtMoney(marginCents, cq.currency)}). Raise the markup and regenerate before sending.
              </div>
            )}
            {isDraft && marginCents > 0 && marginPct < 5 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <strong>Low margin</strong> ({marginPct.toFixed(1)}%). Double-check this is intentional.
              </div>
            )}

            {isDraft && (
              <div className="space-y-3">
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Quantity
                  </div>
                  <input
                    type="number" min={1} step={1}
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                    className="w-24 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Markup % (this quote)
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" step="0.5" min={-100} max={1000}
                      value={markupPct}
                      onChange={(e) => setMarkupPct(Number(e.target.value))}
                      className="w-24 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Validity (days from today)
                  </div>
                  <input
                    type="number" min={1} max={365}
                    value={validityDays}
                    onChange={(e) => setValidityDays(Number(e.target.value))}
                    className="w-24 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <button
                  onClick={() => regenerate.mutate()}
                  disabled={regenerate.isPending || loadingHtml}
                  className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  {regenerate.isPending ? 'Regenerating…' : 'Regenerate (apply changes)'}
                </button>
                <p className="text-[11px] text-muted-foreground italic">
                  Regenerate re-renders with the latest template and the markup / validity above.
                </p>
              </div>
            )}

            {/* Status actions */}
            {isDraft && (
              <button
                onClick={() => patch.mutate('sent')}
                disabled={patch.isPending}
                className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
              >
                {patch.isPending ? 'Updating…' : 'Mark as sent'}
              </button>
            )}
            {cq.status === 'sent' && (
              <div className="space-y-2">
                <button
                  onClick={() => patch.mutate('accepted')}
                  disabled={patch.isPending}
                  className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
                >
                  Mark as accepted
                </button>
                <button
                  onClick={() => patch.mutate('rejected')}
                  disabled={patch.isPending}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                >
                  Mark as rejected
                </button>
                <button
                  onClick={() => patch.mutate('expired')}
                  disabled={patch.isPending}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                >
                  Mark as expired
                </button>
              </div>
            )}
            {/* Recovery from terminal states: an expired quote the customer
                later accepts, or a rejected quote that's revived. The backend
                allows these transitions; without these controls the broker
                would have to mint a brand-new quote. */}
            {cq.status === 'expired' && (
              <div className="space-y-2">
                <button
                  onClick={() => patch.mutate('accepted')}
                  disabled={patch.isPending}
                  className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
                >
                  Mark as accepted
                </button>
                <button
                  onClick={() => patch.mutate('sent')}
                  disabled={patch.isPending}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                >
                  Reactivate (mark as sent)
                </button>
              </div>
            )}
            {cq.status === 'rejected' && (
              <button
                onClick={() => patch.mutate('sent')}
                disabled={patch.isPending}
                className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50"
              >
                Reopen (mark as sent)
              </button>
            )}

            <div className="border-t border-border pt-3 space-y-2">
              <button onClick={downloadHtml} disabled={!html} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50">
                Download .html
              </button>
              <button onClick={copyHtml} disabled={!html} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50">
                Copy HTML to clipboard
              </button>
              <button onClick={openInNewTab} disabled={!html} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50">
                Open in new tab
              </button>
            </div>

            {isDraft && (
              <div className="border-t border-border pt-3">
                <button
                  onClick={() => {
                    if (window.confirm(`Delete draft ${cq.ref}? This can't be undone.`)) remove.mutate();
                  }}
                  disabled={remove.isPending}
                  className="w-full px-3 py-2 text-sm font-medium text-red-700 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
                >
                  {remove.isPending ? 'Deleting…' : 'Delete draft'}
                </button>
              </div>
            )}

            {loadError && <div className="text-sm text-destructive">{loadError}</div>}
            {regenerate.isError && <div className="text-sm text-destructive">{(regenerate.error as { message?: string })?.message ?? 'Regenerate failed'}</div>}
            {patch.isError && <div className="text-sm text-destructive">{(patch.error as { message?: string })?.message ?? 'Status update failed'}</div>}
            {remove.isError && <div className="text-sm text-destructive">{(remove.error as { message?: string })?.message ?? 'Delete failed'}</div>}
          </aside>

          <section className="overflow-hidden bg-[#f4f6fa] flex items-stretch min-h-[55vh] lg:min-h-0">
            {loadingHtml && !html ? (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">Loading quote…</div>
            ) : html ? (
              <iframe ref={iframeRef} title="Quote" className="w-full h-full border-0 bg-white" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">No document to display.</div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
